import { z } from "zod";
import type { Operation } from "../../types";
import { customerPartyDTO, type CustomerPartyDTO } from "../customers";

/**
 * Read contracts for the `_crm/*View` family — RO-3-00d / #44471.
 *
 * ── Why these exist ──
 *
 * `docs/rollover/inventory/shared-render-ownership.md` §6.1 rows 12–23 list the
 * twelve `_crm/*View` files as cause-**A**: each one resolves its own tenant and
 * reads the vault *inside its render path* (`coerceWorkspaceForRead(await
 * resolveTenant())` then `listOrganizations` / `getDb().prepare(...)` /
 * `customerParties`). A server component may do that; a browser component in the
 * Vite host cannot, at any porting effort. So the port is a REWRITE: the vault
 * read moves behind an `Operation`, and the view becomes an ordinary client
 * component that calls `useApi(...)`.
 *
 * One op per view, matching the item's own spec ("one read contract per view").
 * The ops return exactly the rows the view renders — no general-purpose CRM
 * query surface, which would be a different (and unrequested) contract.
 *
 * ── What is deliberately NOT here ──
 *
 * `reports.tsx` gets **no new op**. Its four reads — the pipeline list plus the
 * pipeline / activity / funnel report summaries — are `crm.pipelines.list` and
 * `crm.reports.*`, which already exist AND are already mounted
 * (`excellent/server/routes/crm.ts:568,960,965,970`). Inventing
 * `views.reports` would fork a live contract, so `ReportsView` composes the four.
 *
 * `views.renewal` reuses `customerPartyDTO` from `../customers` rather than
 * re-declaring the 20-field party shape, because `@excellent/ui/blocks/crm/
 * customer-layers` (which `RenewalView` renders through) is already typed
 * against it. It is a SEPARATE op from `customers.portfolio` and not a duplicate
 * of it: portfolio is the whole account-rolled-up base, this is the retention
 * book — the `stage !== "prospect" || lifetimeMinor > 0` subset — filtered
 * server-side where the read model is built. (`customers.portfolio` is in any
 * case not mounted on this host; see the item's evidence.)
 *
 * All four are pure reads: every handler in `routes/crm.ts` mounts them `READ`
 * and touches no write path, transitively included.
 */

// ── accounts (Customers ▸ Accounts) ─────────────────────────────────────────
//
// Accounts ARE `organizations` in the sales context. The self-tagged org (your
// own company, a Directory citizen) is excluded server-side, exactly as the Next
// view's `listSelfEntityIds("organization", …)` filter did.

const accountRow = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  website: z.string().nullable(),
  created_at: z.string(),
});
export type CrmAccountRow = z.infer<typeof accountRow>;

const accountsResponse = z.object({ accounts: z.array(accountRow) });
export type CrmAccountsResponse = z.infer<typeof accountsResponse>;

const accountsOp: Operation<void, void, void, CrmAccountsResponse> = {
  method: "GET",
  path: () => "/api/crm/views/accounts",
  response: accountsResponse,
};

// ── contacts (Customers ▸ Contacts) ─────────────────────────────────────────
//
// Contacts are `people` in the sales context — distinct from Leads. The
// self-tagged person (the workspace owner) is excluded server-side, as in the
// Next view.

const contactRow = z.object({
  id: z.number(),
  uid: z.string(),
  display_name: z.string(),
  given_name: z.string().nullable(),
  family_name: z.string().nullable(),
  created_at: z.string(),
});
export type CrmContactRow = z.infer<typeof contactRow>;

const contactsResponse = z.object({ contacts: z.array(contactRow) });
export type CrmContactsResponse = z.infer<typeof contactsResponse>;

const contactsOp: Operation<void, void, void, CrmContactsResponse> = {
  method: "GET",
  path: () => "/api/crm/views/contacts",
  response: contactsResponse,
};

// ── forecast (Customers ▸ Forecasting) ──────────────────────────────────────
//
// The weighted-pipeline read over `crm_opportunities`: the open book (already
// ordered by weighted value, as the view renders it) plus the closed-won
// aggregate. `crm_opportunities` is absent in a workspace where Sales/CRM never
// migrated, so the handler keeps the source's `tableExists` guard and degrades
// to an empty book / zeros rather than 500ing.

const openDealRow = z.object({
  uid: z.string(),
  name: z.string(),
  value_minor: z.number().nullable(),
  probability_pct: z.number().nullable(),
  expected_close_date: z.string().nullable(),
});
export type CrmOpenDealRow = z.infer<typeof openDealRow>;

const forecastResponse = z.object({
  open: z.array(openDealRow),
  /** Closed-won total, minor units. */
  won_value_minor: z.number(),
  /** Closed-won deal count. */
  won_count: z.number(),
});
export type CrmForecastResponse = z.infer<typeof forecastResponse>;

const forecastOp: Operation<void, void, void, CrmForecastResponse> = {
  method: "GET",
  path: () => "/api/crm/views/forecast",
  response: forecastResponse,
};

// ── renewal (Customers ▸ Renewal) ───────────────────────────────────────────
//
// The retention book: the customer subset of the `customerParties` read model.
// Filtered server-side (a prospect with no revenue is not a renewal subject),
// so the client renders `customers` verbatim and derives its three stat cells
// from them, exactly as the Next view did.

const renewalResponse = z.object({ customers: z.array(customerPartyDTO) });
export type CrmRenewalResponse = { customers: CustomerPartyDTO[] };

const renewalOp: Operation<void, void, void, CrmRenewalResponse> = {
  method: "GET",
  path: () => "/api/crm/views/renewal",
  response: renewalResponse,
};


// ── sequences (list with enrollment counts) ─────────────────────────────────
//
// `sequences.list` returns bare sequence rows. The Next SequencesView also
// rolled up active/total enrollments from `crm_sequence_enrollments` in its
// render path — that JOIN lives here, workspace-scoped via crm_sequences.

const sequenceViewRow = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  enrollments_active: z.number(),
  enrollments_total: z.number(),
});
export type CrmSequenceViewRow = z.infer<typeof sequenceViewRow>;

const sequencesResponse = z.object({ sequences: z.array(sequenceViewRow) });
export type CrmSequencesViewResponse = z.infer<typeof sequencesResponse>;

const sequencesOp: Operation<void, void, void, CrmSequencesViewResponse> = {
  method: "GET",
  path: () => "/api/crm/views/sequences",
  response: sequencesResponse,
};

// ── leads (list bootstrap) ──────────────────────────────────────────────────
//
// LeadsView needs leads + status/source/owner/smart-view labels in one shot so
// LeadsListClient can render without N round-trips. Individual CRUD stays on
// crm.leads.*.

const leadListRow = z.object({
  id: z.number(),
  uid: z.string(),
  display_name: z.string(),
  status_id: z.number().nullable(),
  source_id: z.number().nullable(),
  owner_actor_id: z.number().nullable(),
  last_activity_at: z.string().nullable(),
  created_at: z.string(),
});

const leadsViewResponse = z.object({
  leads: z.array(leadListRow),
  statusLabels: z.record(z.string(), z.string()),
  sourceLabels: z.record(z.string(), z.string()),
  ownerLabels: z.record(z.string(), z.string()),
  smartViews: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
      entity_kind: z.string(),
    }),
  ),
});
export type CrmLeadsViewResponse = z.infer<typeof leadsViewResponse>;

const leadsOp: Operation<void, void, void, CrmLeadsViewResponse> = {
  method: "GET",
  path: () => "/api/crm/views/leads",
  response: leadsViewResponse,
};

// ── support (Customers ▸ Support) ───────────────────────────────────────────
//
// Health strip over won-lead activity + open Customer Success tasks. The Next
// view rendered tasks through UnifiedIssuesList (RO-3-00g); this host returns
// a minimal task DTO the view renders as a plain list until that port lands.

const supportTaskRow = z.object({
  id: z.number(),
  title: z.string(),
  status: z.string(),
  project_slug: z.string().nullable(),
  updated_at: z.string().nullable(),
});

const supportResponse = z.object({
  customers_total: z.number(),
  customers_healthy: z.number(),
  customers_atrisk: z.number(),
  open_tasks: z.array(supportTaskRow),
});
export type CrmSupportViewResponse = z.infer<typeof supportResponse>;

const supportOp: Operation<void, void, void, CrmSupportViewResponse> = {
  method: "GET",
  path: () => "/api/crm/views/support",
  response: supportResponse,
};

export const views = {
  accounts: accountsOp,
  contacts: contactsOp,
  forecast: forecastOp,
  renewal: renewalOp,
  sequences: sequencesOp,
  leads: leadsOp,
  support: supportOp,
};

